import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { useDebounceCallback } from 'usehooks-ts';
import { useClickOutside } from '../../../../../../common/Dropdown';
import DropdownMenu from '../../../../../../common/Dropdown/DropdownMenu';
import styles from '../../Navigator.module.scss';
import useSlideConditions from '../../../useSlideConditions';
import useSlideShowContext from '../../../../stores/useSlideShowContext';
import LockedSlideIcon from './LockedSlideIcon';

const PositionInput = ({ dropdownItemText, numElements, onDirectNav, position, sessionLocked }) => {
  const formattedSlides = useSlideShowContext(state => state.slides);
  const { isSlideLocked } = useSlideConditions();
  const [localPosition, setLocalPosition] = useState(position);
  const [open, setOpen] = useState(false);
  const containerRef = useRef();

  // Debounce typed navigation so multi-digit entry (e.g. "15") jumps once to the
  // final slide instead of navigating through each intermediate value (1, then 15).
  const debouncedNav = useDebounceCallback(onDirectNav, 300);

  useEffect(() => setLocalPosition(position), [position]);

  useClickOutside((e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setOpen(false);
    }
  });

  const positionInputHandler = (e) => {
    if (sessionLocked) return;

    const rawValue = e.target.value;
    setLocalPosition(rawValue);

    const newPosition = Number(rawValue);
    if (!Number.isInteger(newPosition) || newPosition < 1 || newPosition > numElements) {
      setOpen(true);
      return;
    }

    setOpen(false);
    debouncedNav(newPosition - 1);
  };

  const handleDropdownSelect = (slideIndex) => {
    debouncedNav.cancel();
    setOpen(false);
    setLocalPosition((slideIndex + 1).toString());
    onDirectNav(slideIndex);
  };

  return (
    <div className={styles.positionInputContainer}>
      {sessionLocked ? (
        <span>{I18n.t('slide')}</span>
      ) : (
        <label htmlFor="position-input">{I18n.t('slide')}</label>
      )}
      {sessionLocked ? (<span>{position}</span>) : (
        <div ref={containerRef} className={styles.navigatorDropdownButton}>
          <input
            className={styles.positionInput}
            id="position-input"
            inputMode="numeric"
            type="text"
            value={localPosition.toString()}
            onChange={positionInputHandler}
            onClick={() => setOpen(prev => !prev)}
          />
          <DropdownMenu
            className={styles.dropdown}
            open={open}
            position="top"
            role="presentation"
          >
            {new Array(numElements).fill(null).map((_, i) => {
              const locked = isSlideLocked(formattedSlides[i]);

              return (
                <button
                  key={i}
                  aria-current={position === i + 1 ? 'true' : undefined}
                  aria-disabled={locked}
                  className={clsx('dropdown-item', {
                    [styles.dropdownItem]: true,
                    [styles.active]: position === i + 1,
                    [styles.disabled]: locked,
                  })}
                  type="button"
                  onClick={() => {
                    if (locked) return;
                    handleDropdownSelect(i);
                  }}
                >
                  {position === i + 1 && (
                    <i aria-hidden="true" className={`fa fa-solid fa-check ${styles.greenCheck}`} />
                  )}
                  {dropdownItemText} {i + 1}
                  {locked && (
                  <span className={styles.lock}>
                    <LockedSlideIcon slide={formattedSlides[i]} />
                  </span>
                  )}
                </button>
              );
            })}
          </DropdownMenu>
        </div>
      )}
      <span>/ {numElements}</span>
    </div>
  );
};

PositionInput.propTypes = {
  dropdownItemText: PropTypes.string.isRequired,
  numElements: PropTypes.number.isRequired,
  onDirectNav: PropTypes.func.isRequired,
  position: PropTypes.number.isRequired,
  sessionLocked: PropTypes.bool,
};

export default PositionInput;
